import type { BadgeProps } from '@/components/ui/Badge'
import { intlLocale } from '@/i18n'
import type { ExamStatus, ExamTransition } from './types'

export const examStatusLabel: Record<ExamStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  marks_open: 'Marks entry open',
  published: 'Published',
  locked: 'Locked',
}

export const examStatusTone: Record<ExamStatus, NonNullable<BadgeProps['tone']>> = {
  draft: 'neutral',
  scheduled: 'info',
  marks_open: 'warning',
  published: 'success',
  locked: 'brand',
}

export const transitionCopy: Record<
  ExamTransition,
  { label: string; title: string; description: string; tone: 'primary' | 'danger' }
> = {
  schedule: {
    label: 'Schedule',
    title: 'Schedule this exam?',
    description: 'The timetable becomes visible. You can still edit papers until marks entry opens.',
    tone: 'primary',
  },
  unschedule: {
    label: 'Back to draft',
    title: 'Move back to draft?',
    description: 'The exam returns to draft so its setup can be reworked.',
    tone: 'primary',
  },
  'open-marks': {
    label: 'Open marks entry',
    title: 'Open marks entry?',
    description: 'Teachers can then enter marks for their papers. Classes can no longer be changed.',
    tone: 'primary',
  },
  publish: {
    label: 'Publish results',
    title: 'Publish results?',
    description:
      'Results, grades and ranks are calculated and frozen. Families can see them and marks can no longer be edited.',
    tone: 'primary',
  },
  lock: {
    label: 'Lock',
    title: 'Lock this exam?',
    description: 'Locked results are final: the exam can never be reopened.',
    tone: 'danger',
  },
  reopen: {
    label: 'Reopen marks entry',
    title: 'Reopen marks entry?',
    description:
      'Published results are withdrawn and deleted; families no longer see them until you publish again.',
    tone: 'danger',
  },
}

export function progressPercent(entered: number, expected: number): number {
  return expected > 0 ? Math.min(100, Math.round((entered / expected) * 100)) : 0
}

/** "12.50" → "12.5", null → "—" */
export function formatMarks(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return String(Number(value.toFixed(2)))
}

/** Locale-aware score for display ("11,93" in French), at most 2 decimals; null → "—". */
export function formatScore(value: number | null | undefined, minFractionDigits = 0): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'
  return new Intl.NumberFormat(intlLocale(), {
    minimumFractionDigits: minFractionDigits,
    maximumFractionDigits: 2,
  }).format(value)
}

/** "11,93 / 20" */
export function formatOutOf(value: number | null | undefined, outOf: number, minFractionDigits = 0): string {
  return `${formatScore(value, minFractionDigits)} / ${formatScore(outOf)}`
}
